import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next'; // Zastępuje falcon-i18n
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom'; // Zastępuje useNavigate
import { useCartContext } from '../../../context/CartContext';
import { usePopup } from '../../../context/PopupContext';
import { useUserContext } from '../../../context/UserContext';
import useMountState from '../../../hooks/useMountState';
import Button from '../../UI/Button/Button';
import { addToCartEvent } from '../../../hooks/useAnalytics';
import { BuildAbsoluteUrl } from '../../../utils/urlBuilder';

const STATE_NORMAL = 0;
const STATE_ADDING = 1;
const STATE_ADDED = 2;

const AddToCartButton = ({ sku, quantity, name, gallery, categories, selected, ...rest }) => {
  const { t } = useTranslation();
  const { isMounted } = useMountState();
  const { handleProductAdd, refetchCart } = useCartContext();
  const { showAddToCartPopupMessage } = usePopup();
  const { user } = useUserContext();
  const navigate = useNavigate();
  const [buttonState, setButtonState] = useState(STATE_NORMAL);
  const [cartUpdated, setCartUpdated] = useState(false);

  const productUrl = selected?.canonical_url
      ? BuildAbsoluteUrl(selected.canonical_url)
      : rest?.product?.canonical_url
          ? BuildAbsoluteUrl(rest.product.canonical_url)
          : null;

  const shouldBeDisabled = buttonState === STATE_ADDING || buttonState === STATE_ADDED;

  const subscribeToAlert = (uid) => {
    try {
      const encodedUID = atob(uid);
      const redirectPath = `/productalert/add/${encodedUID}${window.location.pathname}`;
      if (!user) {
        navigate(`/customer/login?referrer=${redirectPath}`);
        return;
      }
      navigate(redirectPath);
    } catch (error) {
      console.error('Błąd dekodowania UID:', error);
      showAddToCartPopupMessage(t('messagePopup.alertError'), 'error', 4000);
    }
  };

  const addProductToCart = async () => {
    // Zabezpieczenie przed nieprawidłowymi wartościami
    if (!sku || !quantity || quantity <= 0) {
      showAddToCartPopupMessage(t('messagePopup.qtyZero'), 'error', 4000);
      return;
    }

    setButtonState(STATE_ADDING);
    console.log(`Próba dodania produktu ${sku} do koszyka, ilość: ${quantity}`);

    try {
      await handleProductAdd(sku, quantity);
      if (isMounted()) {
        setButtonState(STATE_ADDED);
        setTimeout(() => {
          if (isMounted()) setButtonState(STATE_NORMAL);
        }, 1000);

        const message = t('messagePopup.addSuccess').replace('[product]', name);
        showAddToCartPopupMessage(message, 'success', 4000);
        console.log(`Produkt ${sku} dodany do koszyka pomyślnie`);

        const productData = selected || rest.product;
        if (productData) {
          addToCartEvent(
              productData,
              quantity,
              productUrl,
              selected ? { categories, gallery } : undefined // Dodajemy kategorie i galerię tylko dla selected
          );
        }
      }
    } catch (err) {
      console.error(`Błąd podczas dodawania produktu ${sku} do koszyka:`, err);
      
      if (isMounted()) {
        let errorMessage;
        
        // Rozszerzona obsługa błędów
        if (err.graphQLErrors && err.graphQLErrors.length > 0) {
          // Obsługa błędów GraphQL
          const graphQLError = err.graphQLErrors[0];
          console.error('GraphQL error:', graphQLError);
          errorMessage = graphQLError.message || t('messagePopup.genericError');
        } else if (err.networkError) {
          // Obsługa błędów sieci
          console.error('Network error:', err.networkError);
          errorMessage = t('messagePopup.networkError');
        } else if (err.message) {
          // Szczegółowa obsługa różnych komunikatów błędów
          if (err.message.includes('requested qty')) {
            errorMessage = t('messagePopup.qtyError');
          } else if (err.message.includes('0')) {
            errorMessage = t('messagePopup.qtyZero');
          } else if (err.message.includes('stock')) {
            errorMessage = t('messagePopup.outOfStock');
          } else {
            errorMessage = t(err.message) || t('messagePopup.genericError');
          }
        } else {
          // Domyślny komunikat błędu
          errorMessage = t('messagePopup.genericError');
        }
        
        showAddToCartPopupMessage(errorMessage, 'error', 4000);
        setButtonState(STATE_NORMAL);
      }
    }
  };

  return (
      <Button
          trait="hover-submit"
          className="sidebar-add-product-submit"
          onClick={addProductToCart}
          disabled={shouldBeDisabled}
          {...rest}
      >
        {buttonState === STATE_NORMAL && t('product.addProduct')}
        {buttonState === STATE_ADDING && t('product.addingProduct')}
        {buttonState === STATE_ADDED && t('product.addedProduct')}
      </Button>
  );
};

AddToCartButton.propTypes = {
  sku: PropTypes.string.isRequired,
  quantity: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  gallery: PropTypes.arrayOf(PropTypes.shape({})),
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  selected: PropTypes.shape({
    canonical_url: PropTypes.string,
    product: PropTypes.object,
  }),
  product: PropTypes.shape({
    canonical_url: PropTypes.string,
  }),
};

export default AddToCartButton;